import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Select, DatePicker, Icon, Spin, Tooltip, Modal, Input, Button } from 'antd';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import SwitchTabs from '../../components/Dashboard/SwitchTabs.js';
import ComplexEmailFailedTransactions from './ComplexEmailFailedTransactions.js';
import TopTenTemplates from './TopTenTemplates.js';
import RadialChart from '../../components/Dashboard/RadialBarChart.js';
import LineChart from '../../components/Dashboard/LineChart.js';
import FunnelChart from './FunnelChart.js';
import { DashboardErrorReport } from './DashboardErrorReport.js';
import { DEFAULT_DATE_TIME_FORMAT, READABLE_DATE_TIME_FORMAT } from '../../utils/helper';

import {
  Dashboard,
  Sidebar,
  Loader,
  Title,
  Filters,
  FailureStatsSection,
  Stats,
  Cards,
  Subtitle,
  FailureReasons,
  LinkText,
  TemplatesList,
  ChartsSection,
  CloseIcon,
  TemplateLink,
  ModalData,
  ModalHeading,
} from './newDashboardStyles.js';

import { getLastTwelveMonths } from '../../utils/javascript.js';
import { EMAIL_DASHBOARD as trans } from './TranslationValues.js';
import { getAllTeams } from '../../utils/services/teamsAPI.js';
import { getComplexEmailDashboardChartData, getComplexEmailTransactionStats, getTopTenFailureComplexEmailDetails } from '../../utils/services/complexEmailAPI.js'
import { getJobRecordDataset } from '../../utils/services/statusMonitoringAPI.js';
import { getAllLanguage } from '../../utils/services/languageApi.js';


const { RangePicker } = DatePicker;
const { Option } = Select;
const NAMESPACE = ['EMAIL_DASHBOARD']

const ComplexEmailDashboard = props => {
  const { t } = props
  const today = new Date();
  const previous = new Date(today.getTime());
  const previousDate = previous.setDate(today.getDate() - 1);
  const startDate = moment(previousDate).format(DEFAULT_DATE_TIME_FORMAT);
  const endDate = moment(today).format(DEFAULT_DATE_TIME_FORMAT);
  const [activeTab, setActiveTab] = useState(0);
  const [templatesView, setTemplatesView] = useState(false);
  const [viewType, setViewType] = useState(null);
  const [sideBarLoader, setSideBarLoader] = useState(false);
  const [chartSectionLoader, setChartSectionLoader] = useState(false);
  const [date, setDate] = useState({
    startDate: startDate,
    endDate: endDate,
  });
  const [selectedTeam, setSelectedTeam] = useState('');
  const [emailPercentages, setEmailPercentages] = useState({});
  const [failedEmailPercentage, setFailedEmailPercentage] = useState(0)
  const [emailStats, setEmailStats] = useState({});
  const [totalEmailConversions, setTotalEmailConversions] = useState(0);
  const [teams, setTeams] = useState([]);
  const [topTenFailureEmails, setTopTenFailureEmails] = useState([]);
  const [chartData, setChartData] = useState({
    emailsSent: [],
    emailsDelivered: [],
    emailsOpened: [],
    emailsClicked: [],
    emailsFailed: [],
    emailsBounced: [],
    emailsWithDataIssue: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ failureDetails: '', payload: '', runtimeError: null });
  const [languages, setLanguages] = useState([]);

  const initialRenderRef = useRef(true);

  const size = {
    sideBarWidth: '38%',
    chartSectionWidth: '62%',
  };

  //set the date on change
  const onDateChange = dates => {
    let startDate = moment(dates[0]).format(DEFAULT_DATE_TIME_FORMAT);
    let endDate = moment(dates[1]).format(DEFAULT_DATE_TIME_FORMAT);
    setDate({
      startDate: startDate,
      endDate: endDate,
    });
  };

  const dashboardAPI = async () => {
    const payload = {
      startDate: date.startDate,
      endDate: date.endDate,
    };
    if (selectedTeam) {
      payload.teamId = selectedTeam;
    }
    const payloadData = { env: activeTab, payload: payload };
    try {
      setSideBarLoader(true);
      const APIs = [getComplexEmailTransactionStats(payloadData)];
      if(initialRenderRef.current) {
        setChartSectionLoader(true);
        APIs.push( getAllLanguage(), getAllTeams(), getComplexEmailDashboardChartData());
        initialRenderRef.current = false;
      }
      if (templatesView && viewType === 1) {
        setChartSectionLoader(true);
        APIs.push(getTopTenFailureComplexEmailDetails(payloadData));
      }
      const apiResponses = await Promise.allSettled(APIs);
      apiResponses.forEach((res) => {
        switch(res.value.statusCode) {
          case 109: {
              setLanguages(res.value.data);
              break;
            }
          case 1712: {
              setEmailPercentages(res.value.data.Percentage);
              setEmailStats(res.value.data.Stats);
              setTotalEmailConversions(res.value.data.conversionRate);
              setFailedEmailPercentage(res.value.data.Percentage.failed);
              break;
            }
          case 1408: {
              setTeams(res?.value?.data);
              break;
            }
          case 1716: {
              chartDataAPI(res?.value);
              break;
            }
          case 1714: {
              setTopTenFailureEmails(res.value.data);
              break;
            }
          default: break;
        }
      });
    } catch(e) {
      console.error(e);
    } finally {
      setSideBarLoader(false);
      setChartSectionLoader(false);
    }
  };

  useEffect(() => {
    dashboardAPI();
  }, [activeTab, selectedTeam, date]);

  const funnelChartData = [
    {
      title: `${t(trans.FUNNEL_SECTION_SENT_LABEL.key, trans.FUNNEL_SECTION_SENT_LABEL.value)}`,
      count: emailStats.sent,
      percentage: emailPercentages.sent,
    },
    {
      title: `${t(trans.FUNNEL_SECTION_DELIVERED_LABEL.key, trans.FUNNEL_SECTION_DELIVERED_LABEL.value)}`,
      count: emailStats.delivered,
      percentage: emailPercentages.delivered,
    },
    {
      title: `${t(trans.FUNNEL_SECTION_OPENS_LABEL.key, trans.FUNNEL_SECTION_OPENS_LABEL.value)}`,
      count: emailStats.opened,
      percentage: emailPercentages.opened,
    },
    {
      title: `${t(trans.FUNNEL_SECTION_CLICKS_LABEL.key, trans.FUNNEL_SECTION_CLICKS_LABEL.value)}`,
      count: emailStats.clicked,
      percentage: emailPercentages.clicked,
    },
  ];
  const funnelChartColors = ['#2463eb', '#93b1f4', '#d4e1fc', '#133176'];


  function disabledDate(current) {
    return current > moment() || current < moment().subtract(7, 'day');
  };

  const handleChange = value => {
    setSelectedTeam(value);
  };

  const topTenFailureAPI = async () => {
    let payload = {
      startDate: date.startDate,
      endDate: date.endDate,
    };
    if (selectedTeam) {
      payload.teamId = selectedTeam;
    }
    try {
      setChartSectionLoader(true);
      const res = await getTopTenFailureComplexEmailDetails({ env: activeTab, payload: payload });

      setTopTenFailureEmails(res.data);
      setChartSectionLoader(false);
    } catch (e) {
      console.log('e:', e);
      setChartSectionLoader(false);
    }
  };

  useEffect(() => {
    if (templatesView && viewType === 1)
      topTenFailureAPI();
  }, [viewType]);


  const chartDataAPI = useCallback(res => {
      const emailsSentArray = Array(12).fill(0);
      const emailsDeliveredArray = Array(12).fill(0);
      const emailsOpenedArray = Array(12).fill(0);
      const emailsClickedArray = Array(12).fill(0);
      const emailsFailedArray = Array(12).fill(0);
      const emailsBouncedArray = Array(12).fill(0);
      const emailsWithDataIssueArray = Array(12).fill(0);

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      for (const data of res.data.getTrendsMetrics) {
        const {
          month,
          year,
          sent,
          delivered,
          opened,
          clicked,
          docstudioFailure,
          bounced,
          dataFailure,
        } = data;
        const monthsAgo = (currentYear - year) * 12 + (currentMonth - month);

        if (monthsAgo >= 0 && monthsAgo < 12) {
          emailsSentArray[11 - monthsAgo] = sent;
          emailsDeliveredArray[11 - monthsAgo] = delivered;
          emailsOpenedArray[11 - monthsAgo] = opened;
          emailsClickedArray[11 - monthsAgo] = clicked;
          emailsFailedArray[11 - monthsAgo] = docstudioFailure;
          emailsBouncedArray[11 - monthsAgo] = bounced;
          emailsWithDataIssueArray[11 - monthsAgo] = dataFailure;
        }
      }
      setChartData({
        emailsSent: emailsSentArray,
        emailsDelivered: emailsDeliveredArray,
        emailsOpened: emailsOpenedArray,
        emailsClicked: emailsClickedArray,
        emailsFailed: emailsFailedArray,
        emailsBounced: emailsBouncedArray,
        emailsWithDataIssue: emailsWithDataIssueArray,
      });
  }, []);


  const showModal = data => {
    setIsModalOpen(true);
    setModalData(data);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setModalData({ failureDetails: '', payload: '', runtimeError: null });
  };

  const handleTemplateIdClick = useCallback(
    templateId => {
      window.open(`/complexEmailDesign/${templateId}`, "_blank");
    },
    [],
  );

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} mailRoom={true}>
        <Input
          placeholder={t(trans.SEARCH_BUTTON.key, trans.SEARCH_BUTTON.value)}
          value={selectedKeys}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          {t(trans.SEARCH_BUTTON.key, trans.SEARCH_BUTTON.value)}
        </Button>
        <Button
          onClick={() => {
            clearFilters();
            confirm();
          }}
          size="small"
          style={{ width: 90 }}
        >
          {t(trans.RESET_BUTTON.key, trans.RESET_BUTTON.value)}
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <Icon type="search" />
    ),
  });

  const getPayload = async transactionId => {
    const res = await getJobRecordDataset(transactionId, 'email');
    if (res && res.statusCode === 5008 && res.data) {
      if (res.data.payload) {
        const dataSet = res.data.payload;
        return dataSet;
      }
    }
    return "";
  };
    const getLanguageNameByCode = (code) => {
      const language = languages.find(lang => lang.code === code);
      return language ? language.name : null; 
    };

  const columns = useMemo(
    () => [
      {
        title: `${t(trans.REQUESTED_TIME_COLUMN_TITLE.key, trans.REQUESTED_TIME_COLUMN_TITLE.value)}`,
        sorter: (a, b) => a.age - b.age,
        dataIndex: 'requestedTime',
        key: 'requestedTime',
        render: date => moment(date).format(READABLE_DATE_TIME_FORMAT),
      },
      {
        title: `${t(trans.REQUESTED_ID_COLUMN_TITLE.key, trans.REQUESTED_ID_COLUMN_TITLE.value)}`,
        dataIndex: 'mailRequestId',
        key: 'mailRequestId',
        ...getColumnSearchProps('mailRequestId'),
      },
      {
        title: `${t(trans.TEMPLATE_COLUMN_TITLE.key, trans.TEMPLATE_COLUMN_TITLE.value)}`,
        dataIndex: 'templateName',
        key: 'templateName',
        render: (name, row) => {
          if (row.templateId) {
            return <TemplateLink onClick={() => handleTemplateIdClick(row.templateId)}>{name}</TemplateLink>
          } else {
            return name;
          }
        },
        ...getColumnSearchProps('templateName'),
      },
      {
        title: `${t(trans.TO_ADDRESS_COLUMN_TITLE.key, trans.TO_ADDRESS_COLUMN_TITLE.value)}`,
        dataIndex: 'toAddress',
        key: 'toAddress',
        render: (name, row) => (
          <TemplateLink onClick={() => handleTemplateIdClick(row.templateId)}>{name}</TemplateLink>
        ),
      },
      {
        title: `${t(trans.FAILURE_REASONS_COLUMN_TITLE.key, trans.FAILURE_REASONS_COLUMN_TITLE.value)}`,
        dataIndex: 'failureReason',
        key: 'failureReason',
        filters: [
          { text: `${t(trans.BOUNCE_RATE_DROPDOWN_OPTION.key, trans.BOUNCE_RATE_DROPDOWN_OPTION.value)}`, value: 'bounce_rate' },
          { text: `${t(trans.DATA_INVALID_DROPDOWN_OPTION.key, trans.DATA_INVALID_DROPDOWN_OPTION.value)}`, value: 'data_invalid' },
          { text: 'DocStudio', value: 'docstudio' },
        ],
        filterMultiple: false,
      },
      {
        title: `${t(trans.FAILURE_DESCRIPTION_COLUMN_TITLE.key, trans.FAILURE_DESCRIPTION_COLUMN_TITLE.value)}`,
        dataIndex: 'failureDesc',
        key: 'failureDesc',
        render: (data, record) => (
          <>
            {data?.length > 30 ? (
              <span>
                <TemplateLink
                  onClick={async () => {
                    const payloadData = await getPayload(record.payload);
                    showModal({
                      failureDetails: data,
                      payload: payloadData,
                    })
                  }}
                >
                  {t(trans.VIEW_FAILURE_DETAILS_ACTION.key, trans.VIEW_FAILURE_DETAILS_ACTION.value)}
                </TemplateLink>
              </span>
            ) : (
              data
            )}
          </>
        ),
      },
      {
        title: `${t(trans.FAILURE_TIME_COLUMN_TITLE.key, trans.FAILURE_TIME_COLUMN_TITLE.value)}`,
        dataIndex: 'failureTime',
        key: 'failureTime',
        render: date => moment(date).format(READABLE_DATE_TIME_FORMAT),
      },
      {
        title: `${t(trans.TEAM_NAME_COLUMN_TITLE.key, trans.TEAM_NAME_COLUMN_TITLE.value)}`,
        dataIndex: 'teamName',
        key: 'teamName',
      },
      {
        title: `${t(trans.LANGUAGE_COLUMN_TITLE.key, trans.LANGUAGE_COLUMN_TITLE.value)}`,
        dataIndex: 'language',
        key: 'language',
        render: (data) => {
          return getLanguageNameByCode(data);
        }
      },
    ],
    [handleTemplateIdClick, t],
  );

  const isPayloadValid = !!modalData?.payload?.replaceAll('{}', '');

  return (
    <Dashboard>
      <Sidebar width={size.sideBarWidth}>
        {sideBarLoader && (
          <Loader>
            <Spin size={'large'} />
          </Loader>
        )}
        <Title height={'10%'}>{t(trans.EMAIL_DASHBOARD_TITLE.key, trans.EMAIL_DASHBOARD_TITLE.value)}</Title>
        <Filters height={'15%'}>
          <SwitchTabs // switch between testing and published
            items={[
              `${t(trans.PUBLISHED_TAB_TITLE.key, trans.PUBLISHED_TAB_TITLE.value)}`,
              `${t(trans.TESTING_TAB_TITLE.key, trans.TESTING_TAB_TITLE.value)}`,
              `${t(trans.DRAFT_TAB_TITLE.key, trans.DRAFT_TAB_TITLE.value)}`,
            ]}
            width={'360px'}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Select // select a team
            showSearch
            style={{ minWidth: '28%' }}
            optionFilterProp="children"
            defaultValue={'All'}
            value={selectedTeam}
            onChange={handleChange}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value="" selected>
              {t(trans.ALL_TEAMS_DROPDOWN_OPTION.key, trans.ALL_TEAMS_DROPDOWN_OPTION.value)}
            </Option>
            {teams.map(team => (
              <Option key={team.id} value={team.id}>
                <Tooltip key={team.id} placement="topLeft" title={team.name}>{team.name}</Tooltip>
              </Option>
            ))}
          </Select>
          <RangePicker // range of date
            disabledDate={disabledDate}
            allowClear={false}
            showTime={{
              hideDisabledOptions: true,
              defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')],
            }}
            defaultValue={[moment().subtract(1, 'day'), moment()]}
            format="YYYY-MM-DD HH:mm:ss"
            onOk={e => onDateChange(e)}
          />
        </Filters>
        <FunnelChart funnelChartData={funnelChartData} funnelChartColors={funnelChartColors} totalEmailConversions={totalEmailConversions} />
        <FailureStatsSection height={'40%'}>
          <Cards
            id={3}
            templatesCount={true}
            animation={'popUp'}
            animationDelay={'400ms'}
            countFontSize={'1.3rem'}
            height={'40%'}
            width={'35%'}
            textColor={'#FFF'}
            bgColor={'#9C179E'}
          >
            <h2 className="title">{t(trans.FAILURE_STATS_CARD_TITLE.key, trans.FAILURE_STATS_CARD_TITLE.value)}</h2>
            <h1 className="count">
              {emailStats.failed}
              <a
                className="linkTag"
                onClick={() => {
                  setViewType(0);
                  setTemplatesView(true);
                }}
              >
                {t(trans.VIEW_LIST_OF_FAILED_TEMPLATES_ACTION.key, trans.VIEW_LIST_OF_FAILED_TEMPLATES_ACTION.value)}
              </a>
            </h1>
            <RadialChart value={failedEmailPercentage} color={['#FADF25']} textColor={'#FFF'} />
          </Cards>
          <Stats>
            <Subtitle height={'20%'}>{t(trans.FAILURE_REASONS_SECTION_TITLE.key, trans.FAILURE_REASONS_SECTION_TITLE.value)}</Subtitle>
            <FailureReasons height={'60%'} width={'100%'}>
              <Cards
                animation={'fadeIn'}
                animationDelay={'400ms'}
                height={'120px'}
                width={'98px'}
                bgColor={'#FFF'}
              >
                <div className="icon"></div>
                <h2 className="text">{t(trans.FAILURE_REASONS_BOUNCE_RATE_CARD_TITLE.key, trans.FAILURE_REASONS_BOUNCE_RATE_CARD_TITLE.value)}</h2>
                <h1 className="percentage">{emailPercentages.bounced}%</h1>
              </Cards>
              <Cards
                animation={'fadeIn'}
                animationDelay={'600ms'}
                height={'120px'}
                width={'98px'}
                bgColor={'#FFF'}
              >
                <div className="icon"></div>
                <h2 className="text">{t(trans.FAILURE_REASONS_DATA_INVALID_CARD_TITLE.key, trans.FAILURE_REASONS_DATA_INVALID_CARD_TITLE.value)}</h2>
                <h1 className="percentage">{emailPercentages.dataFailure}%</h1>
              </Cards>
              <Cards
                animation={'fadeIn'}
                animationDelay={'800ms'}
                height={'120px'}
                width={'98px'}
                bgColor={'#FFF'}
              >
                <div className="icon"></div>
                <h2 className="text">{t(trans.FAILURE_REASONS_DOCSTUDIO_CARD_TITLE.key, trans.FAILURE_REASONS_DOCSTUDIO_CARD_TITLE.value)}</h2>
                <h1 className="percentage">{emailPercentages.docstudioFailure}%</h1>
              </Cards>
            </FailureReasons>
            <LinkText height={'12%'}>
              <a
                onClick={() => {
                  setViewType(1);
                  setTemplatesView(true);
                }}
              >
                {t(trans.VIEW_TEMPLATES_PRONE_TO_FAILURE_ACTION.key, trans.VIEW_TEMPLATES_PRONE_TO_FAILURE_ACTION.value)}
              </a>
            </LinkText>
          </Stats>
        </FailureStatsSection>
      </Sidebar>
      <ChartsSection width={size.chartSectionWidth}>
        {chartSectionLoader && (
          <Loader>
            <Spin size={'large'} />
          </Loader>
        )}
        <LineChart
          title={t(trans.USAGE_TREND_CHART_TITLE.key, trans.USAGE_TREND_CHART_TITLE.value)}
          description={t(trans.USAGE_TREND_CHART_DESCRIPTION.key, trans.USAGE_TREND_CHART_DESCRIPTION.value)}
          height={250}
          width={370}
          colors={['#F55430', '#000']}
          categories={getLastTwelveMonths()}
          series={[
            { name: `${t(trans.USAGE_TREND_SENT_COLOR_LABELS.key, trans.USAGE_TREND_SENT_COLOR_LABELS.value)}`, data: chartData.emailsSent },
            { name: `${t(trans.USAGE_TREND_DELIVERED_COLOR_LABELS.key, trans.USAGE_TREND_DELIVERED_COLOR_LABELS.value)}`, data: chartData.emailsDelivered },
          ]}
        />
        <LineChart
          title={t(trans.EMAIL_PERFORMANCE_CHART_TITLE.key, trans.EMAIL_PERFORMANCE_CHART_TITLE.value)}
          description={t(trans.EMAIL_PERFORMANCE_CHART_DESCRIPTION.key, trans.EMAIL_PERFORMANCE_CHART_DESCRIPTION.value)}
          height={250}
          width={370}
          colors={['#149486', '#000']}
          categories={getLastTwelveMonths()}
          series={[
            { name: `${t(trans.EMAIL_PERFORMANCE_OPEN_RATE_COLOR_LABELS.key, trans.EMAIL_PERFORMANCE_OPEN_RATE_COLOR_LABELS.value)}`, data: chartData.emailsOpened },
            { name: `${t(trans.EMAIL_PERFORMANCE_CLICK_RATE_COLOR_LABELS.key, trans.EMAIL_PERFORMANCE_CLICK_RATE_COLOR_LABELS.value)}`, data: chartData.emailsClicked },
          ]}
        />
        <LineChart
          title={t(trans.EMAIL_STABILITY_CHART_TITLE.key, trans.EMAIL_STABILITY_CHART_TITLE.value)}
          description={t(trans.EMAIL_STABILITY_CHART_DESCRIPTION.key, trans.EMAIL_STABILITY_CHART_DESCRIPTION.value)}
          height={250}
          width={370}
          colors={['#024AAD', '#43E21A', '#E1E2FF']}
          categories={getLastTwelveMonths()}
          series={[
            { name: `${t(trans.EMAIL_STABILITY_FAILED_COLOR_LABELS.key, trans.EMAIL_STABILITY_FAILED_COLOR_LABELS.value)}`, data: chartData.emailsFailed },
            { name: `${t(trans.EMAIL_STABILITY_BOUNCED_COLOR_LABELS.key, trans.EMAIL_STABILITY_BOUNCED_COLOR_LABELS.value)}`, data: chartData.emailsBounced },
            { name: `${t(trans.EMAIL_STABILITY_DATA_ISSUE_COLOR_LABELS.key, trans.EMAIL_STABILITY_DATA_ISSUE_COLOR_LABELS.value)}`, data: chartData.emailsWithDataIssue },
          ]}
        />
      </ChartsSection>
      <TemplatesList screenWidth={size.chartSectionWidth} templatesView={templatesView}>
        <Modal
          className="templates-list"
          title={t(trans.DESCRIPTION_TITLE.key, trans.DESCRIPTION_TITLE.value)}
          visible={isModalOpen}
          onCancel={handleCancel}
          footer={null}
          centered
        >
          <ModalData>
            <DashboardErrorReport failureDetails={modalData.failureDetails}/>
          </ModalData>
          {modalData.payload && (<><ModalHeading>
            {t(trans.PAYLOAD_TITLE.key, trans.PAYLOAD_TITLE.value)}
          </ModalHeading>
            <ModalData>{isPayloadValid ? modalData.payload : t(trans.NO_DETAILS_AVAILABLE_LABEL.key, trans.NO_DETAILS_AVAILABLE_LABEL.value)}</ModalData></>)}
        </Modal>
        <CloseIcon
          onClick={() => {
            setTemplatesView(false);
          }}
        >
          <Icon type="close" />
        </CloseIcon>
        {viewType === 0 ? (
          <ComplexEmailFailedTransactions
            columns={columns}
            title={t(trans.LIST_OF_FAILED_TEMPLATES_TABLE_TITLE.key, trans.LIST_OF_FAILED_TEMPLATES_TABLE_TITLE.value)}
            activeTab={activeTab}
            selectedTeam={selectedTeam}
            date={date}
            setChartSectionLoader={setChartSectionLoader}
            viewType={viewType}
            templatesView={templatesView}
          />
        ) : (
          <TopTenTemplates
            key={() => { topTenFailureEmails.forEach(i => i) }}
            data={topTenFailureEmails}
            title={t(trans.TOP_TEN_TEMPLATES_PRONE_TO_FAILURE_TABLE_TITLE.key, trans.TOP_TEN_TEMPLATES_PRONE_TO_FAILURE_TABLE_TITLE.value)} />
        )}
      </TemplatesList>
    </Dashboard>
  );
};

export default withTranslation(NAMESPACE)(ComplexEmailDashboard);
